import styles from './Reservations.module.css';
import { ShellContent } from '../../components/Shell';
import { Card } from '../../components/Card';
import { FormattedMessage } from 'react-intl';
import { Button } from '../../components/Button';
import { DelayUnmount } from '../../components/DelayUnmount';
import {
  ReserveModal,
  setReserveDialog,
  useReserveDialog,
} from '../../components/ReserveModal';
import {
  deleteReservationSignal,
  useCancelReservation,
  useReservations,
} from '../../services/reservations';
import dayjs from 'dayjs';
import { VehiclePlate } from '../../components/VehiclePlate';
import { PlaceCenter } from '../../components/PlaceCenter';
import { Spinner } from '../../components/Spinner';
import { useConfirmDialog } from '../../components/ConfirmDialogProvider';
import { useCallback, useEffect } from 'react';
import { noop } from 'rxjs';
import { useIsFeatureEnabled } from '../../services/features';
import { Navigate } from 'react-router-dom';

export const Reservations = () => {
  const isOpen = useReserveDialog();
  const { result, running } = useReservations();
  const { open: confirm, close: destroy } = useConfirmDialog();

  const onDelete = useCallback(
    (id: number) => () => {
      confirm(
        <FormattedMessage tagName="p" id="Vehicle.CancelReserve.Confirm" />
      ).then(() => deleteReservationSignal(id), noop);
    },
    [confirm]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => destroy(), []);

  useCancelReservation();

  return (
    <ShellContent>
      <div className={styles.root} data-testid="reservations-page">
        <Button
          onClick={() => setReserveDialog(true)}
          data-testid="reserve-btn"
        >
          <FormattedMessage tagName="div" id="Reserve" />
        </Button>
        <div className={styles.container}>
          <div className={styles.title}>
            <FormattedMessage id="MyReservations" />
          </div>
          <div className={styles.list}>
            {running && (
              <PlaceCenter>
                <Spinner />
              </PlaceCenter>
            )}
            {!running && (
              <>
                {!result?.length && (
                  <span className="light">
                    <FormattedMessage id="Reservations.Empty" />
                  </span>
                )}
                {result?.map(({ id, fromDatetime, toDatetime, objectName }) => (
                  <Card
                    className={styles.card}
                    key={id}
                    data-testid="reservation-card"
                  >
                    <div className={styles.label}>
                      <VehiclePlate>{objectName}</VehiclePlate>
                    </div>
                    <div className={styles.body}>
                      <div>
                        <p>
                          <FormattedMessage id="Date.Start" />
                          :&nbsp;
                          {dayjs(fromDatetime).format('YYYY-MM-DD HH:mm')}
                        </p>
                        <p>
                          <FormattedMessage id="Date.End" />
                          :&nbsp;
                          {dayjs(toDatetime).format('YYYY-MM-DD HH:mm')}
                        </p>
                      </div>
                      <div>
                        <Button variant="text" compact onClick={onDelete(id)}>
                          <FormattedMessage id="Cancel" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
      <DelayUnmount unmount={!isOpen}>
        <ReserveModal open={isOpen} onClose={() => setReserveDialog(false)} />
      </DelayUnmount>
    </ShellContent>
  );
};

export const ReservationsFeature = () =>
  useIsFeatureEnabled('VEHICLE_BOOKING') ? (
    <Reservations />
  ) : (
    <Navigate to="/" replace />
  );
